/**
 * Docks on the warehouse plan (ТЗ «3D-склад — пандус, ворота и наезд камеры», 29.10.2026, section 3.3): the row of
 * gates stands on the room wall nearest to its shipping zone, centred on the zone. One function for the 2D plan, the
 * drawn isometry and the 3D model; the server checks only that the row fits the wall.
 *
 * Coordinates are plan metres: x to the right, y down the plan (the 3D model uses the same x and puts y on its z axis).
 */

export interface MapDockGate {
  location_id: string;
  code: string;
  number: string;
  position: number;
}

/** GET /topology/map → docks: a ramp with its gates; numbers come as Decimal strings. */
export interface MapDock {
  id: string;
  code: string;
  name: string;
  zone_id: string;
  room_id: string | null;
  wall: "auto" | "top" | "bottom" | "left" | "right";
  wall_offset: string | number | null;
  numbering: "ltr" | "rtl";
  pitch: string | number;
  door_width: string | number;
  door_height: string | number;
  ramp_height: string | number;
  ramp_depth: string | number;
  slope: "none" | "left" | "right";
  slope_length: string | number;
  gates: MapDockGate[];
}

export type DockSide = "top" | "bottom" | "left" | "right";

export interface PlanRect {
  x: number;
  y: number;
  length: number; // along x
  width: number; // along y
}

export interface Box2 {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface DockGateLayout {
  gate: MapDockGate;
  /** Centre of the opening on the wall line. */
  x: number;
  y: number;
  /** Distance from the centre of the row along `along`. */
  t: number;
}

export interface DockLayout {
  dock: MapDock;
  side: DockSide;
  /** Outward unit normal of the wall (towards the yard). */
  normal: [number, number];
  /** Unit vector along the wall, to the right of a driver who looks at the gates from the yard. */
  along: [number, number];
  /** Centre of the row on the wall line. */
  center: [number, number];
  wallLength: number;
  rowLength: number;
  /** False when the row is longer than the wall without its 0.5 m corners: it is drawn centred and runs over. */
  fits: boolean;
  gates: DockGateLayout[];
  pitch: number;
  doorWidth: number;
  doorHeight: number;
  rampHeight: number;
  rampDepth: number;
  slopeLength: number;
  /** Ramp, yard and slope on the plan, and everything together (to widen the plan and frame the camera). */
  ramp: Box2;
  yard: Box2;
  slope: Box2 | null;
  bounds: Box2;
}

export const DOCK_YARD_DEPTH = 8; // m of yard drawn in front of the ramp
export const DOCK_PARKING_LINE = 6; // m, the white lines where a truck stands
export const DOCK_SLOPE_WIDTH = 3; // m
const CORNER = 0.5; // m to the corner of the room
const RAMP_EXTRA = 1; // m of ramp beyond the outer gates on each side
const WALL_ORDER: DockSide[] = ["bottom", "right", "top", "left"];

const num = (v: string | number | null | undefined, fallback = 0): number => {
  const n = v === null || v === undefined || v === "" ? NaN : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** The outward normal and «right as seen from the yard» of a wall: right = (−n) × up in the 3D model. */
export function dockAxes(side: DockSide): { normal: [number, number]; along: [number, number] } {
  const normal: [number, number] = side === "top" ? [0, -1] : side === "bottom" ? [0, 1] : side === "left" ? [-1, 0] : [1, 0];
  return { normal, along: [normal[1] === 0 ? 0 : normal[1], normal[0] === 0 ? 0 : -normal[0]] };
}

/** ТЗ 3.3, item 2: the wall nearest to the zone's area; a tie goes to the longer wall, then bottom, right, top, left. */
export function nearestWall(room: PlanRect, zone: PlanRect): DockSide {
  const lengths: Record<DockSide, number> = { top: room.length, bottom: room.length, left: room.width, right: room.width };
  const gaps: Record<DockSide, number> = {
    top: zone.y - room.y,
    bottom: room.y + room.width - (zone.y + zone.width),
    left: zone.x - room.x,
    right: room.x + room.length - (zone.x + zone.length),
  };
  return [...WALL_ORDER].sort((a, b) => gaps[a] - gaps[b] || lengths[b] - lengths[a] || WALL_ORDER.indexOf(a) - WALL_ORDER.indexOf(b))[0];
}

/** The number a gate gets from the card (the server gives the same): prefix + first + (position − 1) × step. */
export function dockGateNumber(prefix: string | null | undefined, first: number, step: number, position: number): string {
  return `${prefix ?? ""}${first + (position - 1) * step}`;
}

function wallOf(room: PlanRect, side: DockSide): { start: [number, number]; length: number } {
  switch (side) {
    case "top":
      return { start: [room.x, room.y], length: room.length };
    case "bottom":
      return { start: [room.x, room.y + room.width], length: room.length };
    case "left":
      return { start: [room.x, room.y], length: room.width };
    default:
      return { start: [room.x + room.length, room.y], length: room.width };
  }
}

/** Place a dock: the wall, the centre of the row and every gate on it, the ramp, the yard and the slope. */
export function layoutDock(dock: MapDock, room: PlanRect, zone: PlanRect | null): DockLayout | null {
  if (!dock.gates.length || room.length <= 0 || room.width <= 0) return null;
  const side: DockSide = dock.wall !== "auto" ? dock.wall : zone ? nearestWall(room, zone) : room.length >= room.width ? "bottom" : "right";
  const { normal, along } = dockAxes(side);
  const wall = wallOf(room, side);
  const horizontal = side === "top" || side === "bottom";
  const pitch = num(dock.pitch, 4);
  const doorWidth = num(dock.door_width, 3);
  const count = dock.gates.length;
  const rowLength = (count - 1) * pitch + doorWidth;
  // the centre as a distance from the left (top) end of the wall, as the card's «Смещение вдоль стены» counts it
  const zoneMid = zone ? (horizontal ? zone.x + zone.length / 2 - wall.start[0] : zone.y + zone.width / 2 - wall.start[1]) : wall.length / 2;
  let mid = dock.wall_offset === null || dock.wall_offset === undefined || dock.wall_offset === "" ? zoneMid : num(dock.wall_offset);
  const fits = rowLength <= wall.length - 2 * CORNER;
  if (fits) mid = Math.min(Math.max(mid, CORNER + rowLength / 2), wall.length - CORNER - rowLength / 2);
  else mid = wall.length / 2;
  const center: [number, number] = horizontal ? [wall.start[0] + mid, wall.start[1]] : [wall.start[0], wall.start[1] + mid];
  const dir = dock.numbering === "rtl" ? -1 : 1;
  const gates = [...dock.gates]
    .sort((a, b) => a.position - b.position)
    .map((gate, i) => {
      const t = dir * (i - (count - 1) / 2) * pitch;
      return { gate, t, x: center[0] + along[0] * t, y: center[1] + along[1] * t };
    });
  const rampDepth = Math.max(0, num(dock.ramp_depth, 2));
  const slopeLength = Math.max(0, num(dock.slope_length, 12));
  const half = rowLength / 2 + RAMP_EXTRA;
  const rect = (t0: number, t1: number, s0: number, s1: number): Box2 => {
    const pts = [
      [t0, s0],
      [t1, s1],
    ].map(([t, s]) => [center[0] + along[0] * t + normal[0] * s, center[1] + along[1] * t + normal[1] * s]);
    return { x0: Math.min(pts[0][0], pts[1][0]), y0: Math.min(pts[0][1], pts[1][1]), x1: Math.max(pts[0][0], pts[1][0]), y1: Math.max(pts[0][1], pts[1][1]) };
  };
  const ramp = rect(-half, half, 0, rampDepth);
  const slope = dock.slope === "left" ? rect(-half - slopeLength, -half, 0, DOCK_SLOPE_WIDTH) : dock.slope === "right" ? rect(half, half + slopeLength, 0, DOCK_SLOPE_WIDTH) : null;
  const yard = rect(-half - (dock.slope === "left" ? slopeLength : 0), half + (dock.slope === "right" ? slopeLength : 0), rampDepth, rampDepth + DOCK_YARD_DEPTH);
  const all = [ramp, yard, ...(slope ? [slope] : [])];
  const bounds = { x0: Math.min(...all.map((b) => b.x0)), y0: Math.min(...all.map((b) => b.y0)), x1: Math.max(...all.map((b) => b.x1)), y1: Math.max(...all.map((b) => b.y1)) };
  return {
    dock,
    side,
    normal,
    along,
    center,
    wallLength: wall.length,
    rowLength,
    fits,
    gates,
    pitch,
    doorWidth,
    doorHeight: num(dock.door_height, 3),
    rampHeight: Math.max(0, num(dock.ramp_height, 1.2)),
    rampDepth,
    slopeLength,
    ramp,
    yard,
    slope,
    bounds,
  };
}

/** The room of a dock: chosen in its card, else the room that holds the zone's centre, else the first sized room. */
export function dockRoom<R extends PlanRect & { id: string }>(dock: MapDock, rooms: R[], zone: PlanRect | null): R | null {
  const own = dock.room_id ? rooms.find((r) => r.id === dock.room_id) : undefined;
  if (own) return own;
  if (zone) {
    const cx = zone.x + zone.length / 2;
    const cy = zone.y + zone.width / 2;
    const holding = rooms.find((r) => cx >= r.x && cx <= r.x + r.length && cy >= r.y && cy <= r.y + r.width);
    if (holding) return holding;
  }
  return rooms[0] ?? null;
}
